import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { CheckboxInput, TextEdit } from "components/table/inputs"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/asset-management/chart/controls/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import chartService, { Chart } from "data/chart"
import { Asset } from "data/asset-list"
import { UserGroup } from "data/user-groups"
import keyService, * as chord from "data/key"
import { handleReferences } from "data/lib/handle-references"
import validationService, { IValidationErrors } from "data/validate"
import { copy, paste, canPaste, getPasteInfo } from "data/lib/copy-paste"
import toggles from "feature-toggles"
// Styles
import { ChartTable_s } from "components/asset-management/chart/chart-styles"

export type ContextRow = {
  chartid: number
  code: string
  name?: string
  sortorder?: number
  systeminput?: string
}

type PropTypes = {
  tableId: string
  data: Chart[]
  assets: Asset[]
  userGroups: UserGroup[]
  selectedState: [ContextRow[], (selected: ContextRow[]) => void]
  flags: {deleteFlag: number, pasteFlag: number}
}

const ChartTable = (props: PropTypes) => {
  const [selected, setSelected] = props.selectedState

  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const traceMenuItem = {
    label: 'Trace', icon: 'pi pi-fw pi-search', command: e=> {
      const refreshFn = () => chartService.fetchCharts()
      handleReferences('chart', contextRow.chartid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      const chart = await chartService.copy(contextRow.chartid, contextRow.code, contextRow.name || '')
      !_.isEmpty(selected) && setSelected(selected.concat(chart as ContextRow))
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const charts = await chartService.copies(contextRow.chartid, n)
        !_.isEmpty(selected) && setSelected(selected.concat(charts as ContextRow[]))
      })
    }},
    traceMenuItem,
    {label: selected.length ? 'Delete Rows' : 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if(contextRow.systeminput) return;
      const pager         = getPagingInfo(props.tableId);
      const currentRowNum = getFocusedRowIndex() + 1
      const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
      showDeleteConfirmation(contextRow, currentRecordIndex + 1)
    }},
  ]

  const systemMenu = [{...traceMenuItem}]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({chartid: 0, code: '', sortorder: 0})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field('chart')
  const [_errors, setErrors] = useState<IValidationErrors>({})

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const chartEventSubscription = chartService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "CHART_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "CHART_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            chartService.create()
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[currentRecordIndex];
            if (!currentRow.systeminput) showDeleteConfirmation({chartid: currentRow.chartid, code: currentRow.code, sortorder: currentRow.sortorder}, currentRecordIndex + 1)
          }
          break
      }
    })

    const validationSubscription = validationService.stream().subscribe((e) => {
      setErrors(e)
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      chartEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
      validationSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    !contextRow.systeminput || (contextRow.systeminput && !contextRow.systeminput.startsWith('system')) ? setMenu(nonSystemMenu) : setMenu(systemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    const chart = field ? _.omit(_.cloneDeep(rowData), 'index') : value
    _.set(chart, field, value);
    chartService.patch(chart);
    if (field === 'isdefault') chartService.fetchCharts()
  }

  const refresh = async () => {
    const charts = await chartService.fetchCharts()
    setSelected(_.intersectionBy(charts, selected, 'chartid'))
  }

  useEffect(() => {
    if(canPaste(contextCell, selected)) {
      nonSystemMenu.splice(1, 0, {label: selected.length ? 'Paste to Rows' : 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => {
        if (selected.length > 1) {
          showPasteConfirmation(contextRow, -1)
          return
        }
        paste(contextCell, contextRow, refresh, 'chart', selected)
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu(systemMenu) : setMenu(nonSystemMenu)
  }, [contextCell, selected.length])

  useEffect(() => {
    if (!props.flags.deleteFlag) return //skip first time
    showDeleteConfirmation(_.first(selected), -1)
  }, [props.flags.deleteFlag])

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      message: `Would you like to delete the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        if (selected.length > 1) {
          chartService.deleteMultiple(_.map(selected, 'chartid'))
        } else {
          chartService.deleteOne(record.chartid)
        }
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code,
      selected
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.chartid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  useEffect(() => {
    if (!props.flags.pasteFlag) return //skip first time
    if (!canPaste(contextCell, selected)) return
    showPasteConfirmation(contextRow, -1)
  }, [props.flags.pasteFlag])

  const showPasteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      title: 'Paste',
      message: `Would you like to paste ${getPasteInfo()._meta.field} data to the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        paste(contextCell, contextRow, refresh, 'chart', selected)
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code,
      selected
    }))
  }

  function updateContextMenu(originalEvent){
    let systemRows = document.querySelectorAll('.system-row, .systemImmutable-row');
    for (let i of systemRows){
      if (originalEvent.target === i || i.contains(originalEvent.target)) {
        setMenu(systemMenu);
      } else {
        if (!menu.length){
          setMenu(nonSystemMenu)
        }
      }
    }
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    if (rowData.systeminput && rowData.systeminput === 'system'){
      return {'system-row' : rowData.systeminput === 'system'}
    }
    else if (rowData.systeminput && rowData.systeminput === 'systemImmutable'){
      return {'systemImmutable-row' : rowData.systeminput === 'systemImmutable'}
    }
    else return {'' : rowData}
  }

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <ChartTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth={toggles.multiselect() ? "90px" : "45px"}
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setSelected([])
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
          selection={selected} onSelectionChange={(e) => {
            const selected = _.filter(e.value, (it) => it.systeminput !== "systemImmutable")
            setSelected(selected)
          }}
        >
          {toggles.multiselect() && <Column selectionMode="multiple" frozen={true} style={{minWidth: "45px", paddingLeft: "8px"}}/>}
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Code"
            {...field('code')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Name"
            {...field('name')}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Default Chart?"
            {...field('isdefault', (rowData, props) => CheckboxInput({persist, ...props}))}
            editor={props => CheckboxInput({persist, ...props})}
          />
          <Column style={{minWidth: "175px"}} className="p-editable-column"
            header="Summary Accounts"
            body={(rowData, props) => <Controls.ChildCount childType="summaryaccount" count={rowData._summaryaccountcount} {...rowData} id={`${props.rowIndex + 1}-summary`} />}
          />
          <Column style={{minWidth: "175px"}} className="p-editable-column"
            header="Detail Accounts"
            body={(rowData, props) => <Controls.ChildCount childType="detailaccount"  count={rowData._detailaccountcount} {...rowData} id={`${props.rowIndex + 1}-detail`} />}
          />
          <Column style={{minWidth: "175px"}} className="p-editable-column"
            header="Cash Flow Map"
            body={(rowData, props) => <Controls.ChildCount childType="cashflowmap"  count={rowData._cashflowmapcount} {...rowData} id={`${props.rowIndex + 1}-cashflowmap`} />}
          />
        </DataTable>
        { carouselData && carouselData['show'] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={500} /> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
        <span className="selection-count">{ selected.length ? `Selected Rows: ${selected.length}`: null}</span>
      </ChartTable_s>
    </div>
  )
}

export default ChartTable
